import { ratesLabels } from 'axl-config';
import findClosestFinanceTerm from './utils/findClosestFinanceTerm.js';

// Helper function to build selected criteria object
function buildSelectedCriteria(baseConfig, updates) {
  return {
    ...baseConfig.selectedCriteria,
    ...updates
  };
}

// Helper function to get interest rate or empty object
function getInterestRate(rate) {
  return Object.keys(rate).length > 0 ? rate : {};
}

// Helper function to update finance terms when credit type changes
function updateFinanceTerms(updatedCreditType, selectedCreditType, availableFinanceTermsByTier, selectedLoanTerm) {
  if (!updatedCreditType || !availableFinanceTermsByTier || updatedCreditType === selectedCreditType) {
    return {
      updatedFinanceTermOptions: null,
      updatedSelectedLoanTerm: null
    };
  }
  const tierKey = updatedCreditType === 'rebuilding' ? 'poor' : updatedCreditType;
  const availableTermsForTier = availableFinanceTermsByTier[tierKey] ?? [];
  if (availableTermsForTier?.length > 0) {
    const newSelectedTerm = findClosestFinanceTerm(selectedLoanTerm, availableTermsForTier);
    return {
      updatedFinanceTermOptions: availableTermsForTier,
      updatedSelectedLoanTerm: newSelectedTerm || null
    };
  }
  return {
    updatedFinanceTermOptions: null,
    updatedSelectedLoanTerm: null
  };
}
async function updateCalculatorConfig() {
  let payments = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    creditType,
    interestRate,
    loanTerm,
    downPayment,
    downPaymentPercent,
    leaseTerm,
    leaseMileageRange,
    interactions: {
      hasUpdatedWallet = false,
      paymentEmphasis = false,
      hasOpenedWallet = false,
      hasRequestedTaxesFees = false
    } = {},
    tradeIn = {}
  } = payments;
  const isWalletActive = hasUpdatedWallet || paymentEmphasis || hasOpenedWallet;
  const {
    value: tradeInValue = 0,
    amountOwed = 0
  } = tradeIn;
  const {
    selectedCreditType,
    interestRate: selectedInterestRate,
    selectedLoanTerm,
    financeDownPayment,
    financeDownPaymentPercentage,
    selectedLeaseTerm,
    selectedLeaseMileage,
    financeTermOptions
  } = config?.selectedCriteria || {};
  const {
    availableFinanceTermsByTier
  } = config || {};
  let updatedCreditType = ratesLabels[creditType]?.decodedValue;
  let updatedFinanceTermOptions = financeTermOptions;
  let updatedSelectedLoanTerm = Number(loanTerm);
  const isBOA = config?.ecommerce === 'BOA';

  // Override credit type if credit tier selection is disabled (e.g., for BOA)
  // This ensures BOA always gets 'excellent' regardless of inventory page selection
  if (isBOA) {
    updatedCreditType = 'excellent';
  }

  // Handle finance terms update when credit type changes
  const financeTermsUpdate = updateFinanceTerms(updatedCreditType, selectedCreditType, availableFinanceTermsByTier, selectedLoanTerm);
  if (financeTermsUpdate.updatedFinanceTermOptions) {
    updatedFinanceTermOptions = financeTermsUpdate.updatedFinanceTermOptions;
  }
  if (financeTermsUpdate.updatedSelectedLoanTerm) {
    updatedSelectedLoanTerm = financeTermsUpdate.updatedSelectedLoanTerm;
  }

  // If we have wallet, update the configuration based on the payments info from it
  if (isWalletActive) {
    const result = {
      ...config,
      selectedCriteria: buildSelectedCriteria(config, {
        selectedLoanTerm: updatedSelectedLoanTerm,
        selectedCreditType: updatedCreditType,
        financeDownPayment: downPayment,
        financeDownPaymentPercentage: downPaymentPercent,
        interestRate: getInterestRate(interestRate),
        selectedLeaseTerm: Number(leaseTerm),
        selectedLeaseMileage: Number(leaseMileageRange),
        financeTermOptions: updatedFinanceTermOptions,
        tradeInValue,
        amountOwed
      })
    };
    if (hasRequestedTaxesFees) {
      result.includeTaxesAndFees = hasRequestedTaxesFees;
    }
    return result;
  }

  // If we don't have wallet, preserve existing configuration state
  return {
    ...config,
    selectedCriteria: buildSelectedCriteria(config, {
      selectedLoanTerm,
      selectedCreditType: isBOA ? 'excellent' : selectedCreditType,
      financeDownPayment,
      financeDownPaymentPercentage,
      interestRate: getInterestRate(selectedInterestRate),
      selectedLeaseTerm,
      selectedLeaseMileage,
      financeTermOptions,
      tradeInValue,
      amountOwed
    })
  };
}
export { updateCalculatorConfig as default };
